import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = params.get("token") || "";
    if (!t) {
      toast.error("Reset link is missing its token — request a new one.");
    }
    setToken(t);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (typeof setUser === "function") setUser(data);
      toast.success("Password reset. You're logged in.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-12" data-testid="reset-root">
      <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-3">/ PASSWORD RESET</div>
      <h1 className="font-heading text-4xl font-black tracking-tighter uppercase mb-3">Set new password</h1>
      <p className="text-sm text-gray-400 mb-8">Choose a fresh password. You'll be logged in as soon as it saves.</p>

      <form onSubmit={onSubmit} className="space-y-5 border border-[#232330] bg-[#101015] p-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-gray-400">New password</Label>
          <Input
            data-testid="reset-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 focus:ring-cyan-400/40"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-gray-400">Confirm</Label>
          <Input
            data-testid="reset-confirm"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 focus:ring-cyan-400/40"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !token}
          data-testid="reset-submit"
          className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest flex items-center justify-center gap-2"
        >
          <KeyRound className="w-4 h-4" /> {busy ? "Saving…" : "Reset & log in"}
        </Button>
        <div className="text-sm text-gray-500 text-center">
          Need a new link?{" "}
          <Link to="/forgot-password" className="text-cyan-400 hover:underline" data-testid="reset-request-new">
            Request one
          </Link>
        </div>
      </form>
    </div>
  );
}
